import React, { useState } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Card,
  CardMedia,
  CardContent,
  Button,
  Rating,
  useMediaQuery,
  useTheme,
  Dialog,
  Snackbar,
  Alert,
} from '@mui/material';
import { Close, Favorite, Share } from '@mui/icons-material';
import { useFavorites } from '../contexts/FavoritesContext';
import { eventAPI } from '../services/api';

interface FavoritesSidebarProps {
  open: boolean;
  onClose: () => void;
}

const FavoritesSidebar: React.FC<FavoritesSidebarProps> = ({ open, onClose }) => {
  const { favorites, removeFromFavorites } = useFavorites();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [expandedDescriptions, setExpandedDescriptions] = useState<Set<string>>(new Set());
  const [shareSnackbarOpen, setShareSnackbarOpen] = useState(false);

  const handleViewClick = async (event: any) => {
    try {
      await eventAPI.incrementClick(event.uniqueId);
      window.open(event.affiliateUrl, '_blank', 'noopener,noreferrer');
    } catch (error) {
      console.error('Error tracking click:', error);
      window.open(event.affiliateUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const handleRemoveFavorite = (eventId: string) => {
    removeFromFavorites(eventId);
  };

  const toggleDescription = (eventId: string) => {
    const newExpanded = new Set(expandedDescriptions);
    if (newExpanded.has(eventId)) {
      newExpanded.delete(eventId);
    } else {
      newExpanded.add(eventId);
    }
    setExpandedDescriptions(newExpanded);
  };

  const handleShare = async (event: any) => {
    const shareData = {
      title: event.eventTitle,
      text: event.subhead || `Experience ${event.eventTitle}`,
      url: event.affiliateUrl,
    };

    // Check if device supports native sharing
    if (navigator.share && /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
      try {
        await navigator.share(shareData);
      } catch (error) {
        console.error('Error sharing:', error);
      }
    } else {
      // Desktop: Copy to clipboard
      try {
        await navigator.clipboard.writeText(event.affiliateUrl);
        setShareSnackbarOpen(true);
      } catch (error) {
        console.error('Error copying to clipboard:', error);
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = event.affiliateUrl;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        setShareSnackbarOpen(true);
      }
    }
  };

  const content = (
    <Box
      sx={{
        width: isMobile ? '100vw' : 400,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: 'linear-gradient(135deg, #fafafa 0%, #ffffff 100%)',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 3,
          borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
          background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
          color: 'white',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Favorite />
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Saved
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{
            color: 'white',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
            },
          }}
        >
          <Close />
        </IconButton>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        {favorites.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '60%',
              textAlign: 'center',
              color: 'text.secondary',
            }}
          >
            <Favorite sx={{ fontSize: 64, color: '#e5e7eb', mb: 2 }} />
            <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
              No favorites yet
            </Typography>
            <Typography variant="body2">
              Start adding events to your favorites to see them here!
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {favorites.map((event) => (
              <Card
                key={event.uniqueId}
                sx={{
                  borderRadius: 1.5,
                  overflow: 'hidden',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
                  },
                }}
              >
                {/* Image at top with X overlay */}
                <Box sx={{ position: 'relative' }}>
                  <CardMedia
                    component="img"
                    sx={{ 
                      width: '100%',
                      height: 160,
                      objectFit: 'cover'
                    }}
                    image={event.imageUrl}
                    alt={event.eventTitle}
                  />
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveFavorite(event.uniqueId)}
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      color: '#ef4444',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 1)',
                        color: '#dc2626',
                      },
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                    }}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </Box>
                
                {/* Content below image */}
                <CardContent sx={{ p: 2, pb: 1 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      lineHeight: 1.2,
                      mb: 1,
                      fontSize: '1rem',
                    }}
                  >
                    {event.eventTitle}
                  </Typography>
                  
                  {event.price && (
                    <Typography 
                      variant="subtitle2" 
                      sx={{ 
                        color: '#059669',
                        fontWeight: 700,
                        fontSize: '0.9rem',
                        mb: 1,
                      }}
                    >
                      {event.price}
                    </Typography>
                  )}
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Rating value={event.starRating} readOnly size="small" />
                    <Typography variant="caption" sx={{ ml: 0.5 }}>
                      ({event.starRating})
                    </Typography>
                  </Box>

                  {event.category && (
                    <Box sx={{ mb: 1 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          background: event.category === 'hotel' ? '#6366f1' : 
                                     event.category === 'vacation_rentals' ? '#6366f1' :
                                     event.category === 'activities' ? '#0ea5e9' : '#f59e0b',
                          color: 'white',
                          px: 1,
                          py: 0.5,
                          borderRadius: 1,
                          fontSize: '0.7rem',
                          fontWeight: 500,
                          textTransform: 'capitalize',
                        }}
                      >
                        {event.category === 'activities' ? 'Activities' : 
                         event.category === 'hotel' ? 'Hotel' : 
                         event.category === 'vacation_rentals' ? 'Vacation Rental' : 'Event'}
                      </Typography>
                    </Box>
                  )}
                  
                  {event.subhead && (
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'text.secondary',
                          fontSize: '0.85rem',
                          cursor: 'pointer',
                          '&:hover': { color: 'primary.main' },
                          ...(!expandedDescriptions.has(event.uniqueId) && {
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          })
                        }}
                        onClick={() => toggleDescription(event.uniqueId)}
                      >
                        {event.subhead}
                      </Typography>
                      {event.subhead.length > 100 && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'primary.main',
                            cursor: 'pointer',
                            fontSize: '0.7rem',
                            fontWeight: 500,
                            '&:hover': { textDecoration: 'underline' }
                          }}
                          onClick={() => toggleDescription(event.uniqueId)}
                        >
                          {expandedDescriptions.has(event.uniqueId) ? 'Show less' : 'Show more'}
                        </Typography>
                      )}
                    </Box>
                  )}
                </CardContent>
                
                {/* Buttons at bottom */}
                <Box
                  sx={{
                    display: 'flex',
                    gap: 0.5,
                    p: 2,
                    pt: 0,
                    alignItems: 'center',
                  }}
                >
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => handleViewClick(event)}
                    sx={{
                      fontSize: '0.75rem',
                      textTransform: 'none',
                      flex: 1,
                      background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                      }
                    }}
                  >
                    Book Now ⇗
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => handleShare(event)}
                    startIcon={<Share sx={{ fontSize: '0.9rem' }} />}
                    sx={{
                      color: '#6366f1',
                      borderColor: '#6366f1',
                      fontSize: '0.75rem',
                      textTransform: 'none',
                      minWidth: 'auto',
                      '&:hover': {
                        background: 'rgba(99, 102, 241, 0.1)',
                        borderColor: '#4f46e5',
                      },
                      px: 1.5,
                    }}
                  >
                    Share
                  </Button>
                </Box>
              </Card>
            ))}
          </Box>
        )}
      </Box>
      
      <Snackbar
        open={shareSnackbarOpen}
        autoHideDuration={3000}
        onClose={() => setShareSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setShareSnackbarOpen(false)} 
          severity="success" 
          sx={{ width: '100%' }}
        >
          Link copied to clipboard!
        </Alert>
      </Snackbar>
    </Box>
  );

  if (isMobile) {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        fullScreen
        PaperProps={{
          sx: {
            background: 'linear-gradient(135deg, #fafafa 0%, #ffffff 100%)',
          },
        }}
      >
        {content}
      </Dialog>
    );
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          background: 'linear-gradient(135deg, #fafafa 0%, #ffffff 100%)',
        },
      }}
    >
      {content}
    </Drawer>
  );
};

export default FavoritesSidebar;